import { store, closeSidePanel } from '@/app/store';
import { Csv, Excel, Pdf, Placeholder, Ppt, Rar, Txt, Word, Zip } from '@/assets/images';
import { setPostPreview } from '@/features/chats/model/mediaPreview';
import {
   ALLOWED_ALL_FILE_FORMATS,
   AUDIO_FORMATS,
   DOCUMENT_FORMATS,
   IMAGE_FORMATS,
   MEDIA_TYPES,
   MIN_OFFSET_HEIGHT,
   MIN_OFFSET_WIDTH,
   VIDEO_FORMATS,
} from '@/shared/constants/mediaConstants';
import { getExtension } from '@/shared/utils/mediaUtils';

/**
 * Get file icon based on file type and extension
 * @param {string} fileType - MIME type of the file
 * @param {string} extension - File extension (with or without dot)
 * @returns {string} Icon/image URL for the file type
 */

export const getFileFromType = (fileType, extension) => {
   const getLowercase = extension ? extension.toString().toLowerCase() : '';
   const ext = getLowercase.startsWith('.') ? getLowercase : `.${getLowercase}`;

   if (
      ext === '.doc' ||
      ext === '.docx' ||
      fileType === 'application/msword' ||
      fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
   ) {
      return Word;
   } else if (
      ext === '.xls' ||
      ext === '.xlsx' ||
      fileType === 'application/vnd.ms-excel' ||
      fileType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
   ) {
      return Excel;
   } else if (
      ext === '.txt' ||
      ext === '.json' ||
      ext === '.xml' ||
      fileType === 'text/plain' ||
      fileType === 'application/json' ||
      fileType === 'application/xml' ||
      fileType === 'text/xml'
   ) {
      return Txt;
   } else if (ext === '.pdf' || fileType === 'application/pdf') {
      return Pdf;
   } else if (ext === '.csv' || fileType === 'text/csv' || fileType === 'application/csv') {
      return Csv;
   } else if (
      ext === '.ppt' ||
      ext === '.pptx' ||
      fileType === 'application/vnd.ms-powerpoint' ||
      fileType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
   ) {
      return Ppt;
   } else if (
      ext === '.zip' ||
      ext === '.7z' ||
      fileType === 'application/zip' ||
      fileType === 'application/x-zip-compressed' ||
      fileType === 'application/octet-stream' ||
      fileType === 'application/x-7z-compressed'
   ) {
      return Zip;
   } else if (ext === '.rar' || fileType === 'application/rar' || fileType === 'application/vnd.rar') {
      return Rar;
   } else {
      return Placeholder;
   }
};

/**
 * Determine message type (image, video, audio, file) from MIME type and file info.
 * @param {string} fileType - MIME type
 * @param {File} file - File object (for name extension check)
 * @returns {string|undefined} Message type or undefined if unknown
 */
export const getMessageType = (fileType, file) => {
   let msgType;
   const allowedFilescheck = new RegExp('([a-zA-Z0-9s_\\\\.-:])+(' + ALLOWED_ALL_FILE_FORMATS.join('|') + ')$', 'i');

   if (IMAGE_FORMATS.includes(fileType)) {
      msgType = MEDIA_TYPES.IMAGE;
   } else if (VIDEO_FORMATS.includes(fileType)) {
      msgType = MEDIA_TYPES.VIDEO;
   } else if (AUDIO_FORMATS.includes(fileType)) {
      msgType = MEDIA_TYPES.AUDIO;
   } else if (allowedFilescheck.test(getExtension(file.name)) || DOCUMENT_FORMATS.includes(fileType)) {
      msgType = MEDIA_TYPES.FILE;
   }
   return msgType;
};

/**
 * Recalculates dimensions based on an offset container size.
 */
export const calculateWidthAndHeightOffset = (width, height, offsetWidth, offsetHeight) => {
   if (width < offsetWidth && height < offsetHeight) {
      if (width < MIN_OFFSET_WIDTH && height < MIN_OFFSET_HEIGHT) {
         if (width === height) return { webWidth: MIN_OFFSET_WIDTH, webHeight: MIN_OFFSET_HEIGHT };

         if (width > height) {
            return {
               webWidth: MIN_OFFSET_WIDTH,
               webHeight: Math.round((height / width) * MIN_OFFSET_WIDTH),
            };
         }
         return {
            webWidth: Math.round((width / height) * MIN_OFFSET_HEIGHT),
            webHeight: MIN_OFFSET_HEIGHT,
         };
      }
      return { webWidth: width, webHeight: height };
   }

   if (height > offsetHeight) {
      const resultWidth = Math.round(offsetHeight * (width / height));
      return {
         webWidth: Math.min(resultWidth, offsetWidth),
         webHeight: offsetHeight,
      };
   }

   if (width > height) {
      return {
         webWidth: offsetWidth,
         webHeight: Math.round(offsetWidth / (width / height)),
      };
   }

   return { webWidth: offsetWidth, webHeight: offsetWidth };
};

export const handleSelectMedia = ({ message = '', activeChatJid = '', chatType = '' }) => {
   store.dispatch(closeSidePanel());
   store.dispatch(setPostPreview({ messageId: message.msgId, chatId: activeChatJid, chatType }));
};
